import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

interface Product {
  sku: string;
  name?: string;
  warehouse: number;
  reserved: number;
  antaka: string;
}

export default function EditInventoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editForm, setEditForm] = useState({
    sku: '',
    name: '',
    warehouse: 0,
    reserved: 0,
    antaka: 'متاح',
  });
  const [selectedColor, setSelectedColor] = useState('');
  const [colors] = useState(['أسود', 'أبيض', 'أحمر', 'أزرق', 'بني', 'بيج', 'وردي', 'ذهبي', 'فضي', 'هافان']);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Check Owner access
  useEffect(() => {
    if (user?.role !== 'Owner') {
      navigate('/inventory');
    }
  }, [user, navigate]);

  // Load products
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await fetch('/inventory-data.json');
        const data = await response.json();
        setProducts(data.products);
        setFilteredProducts(data.products);
      } catch (error) {
        console.error('Failed to load products:', error);
      }
    };
    loadProducts();
  }, []);

  // Handle search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(
        (p) =>
          p.sku.includes(searchTerm) ||
          (p.name && p.name.includes(searchTerm))
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setSelectedColor('');
    setEditForm({
      sku: product.sku,
      name: product.name || '',
      warehouse: product.warehouse,
      reserved: product.reserved,
      antaka: product.antaka,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditForm({
      ...editForm,
      [name]: name === 'name' || name === 'antaka' ? value : parseInt(value) || 0,
    });
  };

  const handleSave = async () => {
    if (!selectedProduct) return;

    setLoading(true);
    setMessage(null);

    try {
      // Update product (name + antaka status)
      if (editForm.name !== selectedProduct.name || editForm.antaka !== selectedProduct.antaka) {
        await fetch(
          `${import.meta.env.VITE_API_URL || 'http://localhost:8787'}/api/inventory/products/${editForm.sku}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify({ name: editForm.name, antaka: editForm.antaka }),
          }
        );
      }

      // Update stock
      if (
        editForm.warehouse !== selectedProduct.warehouse ||
        editForm.reserved !== selectedProduct.reserved
      ) {
        await fetch(
          `${import.meta.env.VITE_API_URL || 'http://localhost:8787'}/api/inventory/stock/${editForm.sku}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify({
              warehouse: editForm.warehouse,
              reserved: editForm.reserved,
              total: editForm.warehouse + editForm.reserved,
            }),
          }
        );
      }

      // Update local state
      const updatedProducts = products.map((p) =>
        p.sku === editForm.sku
          ? {
              ...p,
              name: editForm.name,
              warehouse: editForm.warehouse,
              reserved: editForm.reserved,
              antaka: editForm.antaka,
            }
          : p
      );
      setProducts(updatedProducts);
      setFilteredProducts(
        filteredProducts.map((p) =>
          p.sku === editForm.sku
            ? {
                ...p,
                name: editForm.name,
                warehouse: editForm.warehouse,
                reserved: editForm.reserved,
                antaka: editForm.antaka,
              }
            : p
        )
      );
      setSelectedProduct(null);
      setMessage({ type: 'success', text: '✅ تم الحفظ بنجاح!' });
    } catch (error) {
      setMessage({ type: 'error', text: `❌ خطأ: ${String(error)}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">📝 تعديل المنتجات</h1>
        <p className="text-gray-600">تعديل المخزن والمورد والألوان - صلاحيات مالك فقط</p>
      </div>

      {/* Alert */}
      {message && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Products List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-4">
            {/* Search Bar */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="🔍 ابحث عن الكود أو الاسم..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Products */}
            <div className="max-h-96 overflow-y-auto space-y-2">
              {filteredProducts.map((product) => (
                <button
                  key={product.sku}
                  onClick={() => handleSelectProduct(product)}
                  className={`w-full text-left p-3 rounded border-2 transition ${
                    selectedProduct?.sku === product.sku
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-mono text-sm font-bold text-blue-600">{product.sku}</div>
                  <div className="text-sm text-gray-700 truncate">{product.name || 'بدون اسم'}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {product.antaka === 'متاح' ? '✅ متاح عند المورد' : '❌ غير متاح'}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Edit Form */}
        <div className="lg:col-span-2">
          {selectedProduct ? (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-6">تعديل: {selectedProduct.sku}</h2>

              <div className="space-y-4">
                {/* SKU (Read-only) */}
                <div>
                  <label className="block text-sm font-semibold mb-2">الكود (SKU)</label>
                  <input
                    type="text"
                    value={editForm.sku}
                    disabled
                    className="w-full px-4 py-2 border rounded-lg bg-gray-100 text-gray-600"
                  />
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold mb-2">اسم المنتج</label>
                  <input
                    type="text"
                    name="name"
                    value={editForm.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="أدخل اسم المنتج"
                  />
                </div>

                {/* Color Selection */}
                <div>
                  <label className="block text-sm font-semibold mb-2">اختر اللون (اختياري)</label>
                  <select
                    value={selectedColor}
                    onChange={(e) => setSelectedColor(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">اختر لون...</option>
                    {colors.map((color) => (
                      <option key={color} value={color}>
                        {color}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Warehouse */}
                <div>
                  <label className="block text-sm font-semibold mb-2">في المخزن (بيت عم حسن)</label>
                  <input
                    type="number"
                    name="warehouse"
                    value={editForm.warehouse}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* Reserved */}
                <div>
                  <label className="block text-sm font-semibold mb-2">محجوز عند المورد (أنتاكا)</label>
                  <input
                    type="number"
                    name="reserved"
                    value={editForm.reserved}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                {/* Antaka Status */}
                <div>
                  <label className="block text-sm font-semibold mb-2">متاح عند المورد؟</label>
                  <select
                    name="antaka"
                    value={editForm.antaka}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="متاح">✅ متاح</option>
                    <option value="غير متاح">❌ غير متاح</option>
                  </select>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <div>
                    <p className="text-gray-600 text-sm">الإجمالي</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {editForm.warehouse + editForm.reserved}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm">الفرق</p>
                    <p
                      className={`text-2xl font-bold ${
                        editForm.warehouse + editForm.reserved ===
                        selectedProduct.warehouse + selectedProduct.reserved
                          ? 'text-gray-400'
                          : 'text-orange-600'
                      }`}
                    >
                      {editForm.warehouse +
                        editForm.reserved -
                        (selectedProduct.warehouse + selectedProduct.reserved)}
                    </p>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'جاري الحفظ...' : '💾 حفظ التغييرات'}
                  </button>
                  <button
                    onClick={() => setSelectedProduct(null)}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-400"
                  >
                    إلغاء
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
              🔍 ابحث عن منتج أو اختره من القائمة لتعديله
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
